import moment from "moment";
import _compact from "lodash/compact";
import { UserTypes } from "shared-types";
import { TFunction } from "i18n";
import { getPlayingAgeTitle } from "shared-utils";
import { UserClaimTypes, UserManager, UserRoleManager } from "core";
import { MUSICAL_NOTES } from "shared/modules/BasicInfo/components/EditBasicInfo";

import { sortArray } from "shared/utils/array";

import { BasicInfo, FacetCategories, MeasurementTypes } from "models";
import {
  IBasicInfoViewModel,
  IFacetCategoryViewModel,
  IFacetsViewModel,
  IFacetViewModel,
} from "../viewModels";

const BASIC_INFO_CONVERTIBLE_WEIGHT_MEASUREMENTS_MAPPING = {
  [MeasurementTypes.Weight]: "weight",
};

const BASIC_INFO_CONVERTIBLE_HEIGHT_MEASUREMENTS_MAPPING = {
  [MeasurementTypes.Height]: "height",
};

const BASIC_INFO_CONVERTIBLE_LENGTH_MEASUREMENTS_MAPPING = {
  [MeasurementTypes.Chest]: "chestSize",
  [MeasurementTypes.Waist]: "waistSize",
  [MeasurementTypes.Hips]: "hipsSize",
  [MeasurementTypes.InsideLeg]: "insideLegSize",
  [MeasurementTypes.InsideArm]: "insideArmSize",
  [MeasurementTypes.Collar]: "collarSize",
  [MeasurementTypes.Hat]: "hatSize",
};

const BASIC_INFO_FORM_MEASUREMENTS_MAPPING = {
  [MeasurementTypes.Shoes]: "shoeSize",
  [MeasurementTypes.Dress]: "dressSize",
  ...BASIC_INFO_CONVERTIBLE_WEIGHT_MEASUREMENTS_MAPPING,
  ...BASIC_INFO_CONVERTIBLE_HEIGHT_MEASUREMENTS_MAPPING,
  ...BASIC_INFO_CONVERTIBLE_LENGTH_MEASUREMENTS_MAPPING,
};

const BASIC_INFO_FORM_MULTIPLE_FACETS_MAPPING = {
  [FacetCategories.MembershipBodies]: "memberships",
  [FacetCategories.Nationality]: "nationalities",
  [FacetCategories.Appearance]: "appearances",
  [FacetCategories.Disability]: "disabilities",
  [FacetCategories.Gender]: "genders",
  [FacetCategories.HeritageAndEthnicity]: "heritageAndEthnicity",
  [FacetCategories.EthnicBackground]: "ethnicBackground",
  [FacetCategories.VoiceStyle]: "voiceStyle",
  [FacetCategories.VisasAndWorkingPermits]: "visasAndPermits",
};

const BASIC_INFO_FORM_SINGLE_FACETS_MAPPING = {
  [FacetCategories.EyeColour]: "eyeColor",
  [FacetCategories.HairColour]: "hairColor",
  [FacetCategories.HairLength]: "hairLength",
  [FacetCategories.FacialHair]: "facialHair",
};

export interface IBasicInfoConverterContent {
  detailsConnectorTo: string;
  detailsYears: string;
  detailsMonths: string;
  detailsStones: string;
  detailsPounds: string;
  detailsBeltTo: string;
  detailsYesMark: string;
  detailsNoMark: string;
}

const GENDER_CODES_IN_PRIORITY = [
  "Gender_Other_Custom",
  "Gender_Other_NonBinary",
  "Gender_Other_Transgenderfemale",
  "Gender_Other_Transgendermale",
  "Gender_Other_Male",
  "Gender_Other_Female",
];

export class BasicInfoConverter {
  public static convertToView(
    basicInfo: BasicInfo,
    facets: IFacetsViewModel,
    content: IBasicInfoConverterContent,
    t: TFunction,
    isViewMode: boolean
  ): IBasicInfoViewModel {
    let model = {} as IBasicInfoViewModel;

    const userType = UserRoleManager.getUserType();
    const isYoungPerformer =
      userType === UserTypes.youngPerformerFemale ||
      userType === UserTypes.youngPerformerMale;

    model = this.mapViewMeasurements(model, basicInfo, content);
    model = this.mapViewFacets(model, basicInfo, facets);
    model = this.mapVocalRange(model, basicInfo, facets, content);
    model = this.mapVocalAge(model, basicInfo, content);

    model.locations = sortArray(basicInfo.locations, [
      { expression: (item) => item.position, order: "asc" },
    ]).map((location) => location.value);

    if (basicInfo.age && basicInfo.isVisibleActualAge) {
      model.actualAge = basicInfo.age;
    }

    if (isYoungPerformer) {
      model.licenseRequired = basicInfo.isLicenseRequired
        ? content.detailsYesMark
        : content.detailsNoMark;
    }

    model.hasTwin = basicInfo.hasTwin ? content.detailsYesMark : undefined;
    model.makeUp = basicInfo.makeUp;
    model.pregnancy = basicInfo.pregnantUntil
      ? moment(basicInfo.pregnantUntil).format("DD/MM/YYYY")
      : undefined;

    const playingAgeFrom = getPlayingAgeTitle(t, basicInfo?.playingAgeFrom);

    const playingAgeTo = getPlayingAgeTitle(t, basicInfo?.playingAgeTo);

    model.playingAge = t("performer:profile.basicInfo.playingAgeValue", {
      from: playingAgeFrom,
      to: playingAgeTo,
    });

    const isVisibilityGenderSuppressed =
      !isViewMode || UserManager.hasClaimValue(UserClaimTypes.isGenderVisible);
    const isVisibilityDisabilitiesSuppressed =
      !isViewMode ||
      UserManager.hasClaimValue(UserClaimTypes.isDisabilitiesVisible);
    model.disabilities = this.rearrangeDisabilities(
      model.disabilities,
      basicInfo,
      content,
      isVisibilityDisabilitiesSuppressed
    );
    model.genders = this.rearrangeGenders(
      model.genders,
      basicInfo,
      isVisibilityGenderSuppressed
    );

    const heritageAndEthnicityVisibility =
      !isViewMode || basicInfo.isVisibleHeritageAndEthnicity;

    if (
      (model.ethnicBackground ||
        model.heritageAndEthnicity ||
        basicInfo?.heritageAndEthnicityInfo) &&
      heritageAndEthnicityVisibility
    ) {
      model.ethnicity = _compact([
        ...(model.ethnicBackground || []),
        ...(model.heritageAndEthnicity || []),
        basicInfo?.heritageAndEthnicityInfo || "",
      ]);
    }

    return model;
  }

  private static rearrangeDisabilities(
    disabilities: string[] = [],
    basicInfo: BasicInfo,
    content: IBasicInfoConverterContent,
    isVisibilitySettingSuppressed: boolean
  ): string[] {
    const result = new Array<string>();
    const { disabilityInfo = "" } = basicInfo;

    if (basicInfo.isVisibleDisability || isVisibilitySettingSuppressed) {
      if (basicInfo.hasDisability) {
        result.push(...disabilities);

        if (disabilities.length === 0 && disabilityInfo.trim() === "") {
          result.push(content.detailsYesMark);
        }

        if (disabilityInfo) {
          result.push(disabilityInfo);
        }
      }
    }

    return result;
  }

  private static rearrangeGenders(
    genders: string[] = [],
    basicInfo: BasicInfo,
    isVisibilitySettingSuppressed: boolean
  ): string[] {
    const result = new Array<string>();

    if (basicInfo.isVisibleGender || isVisibilitySettingSuppressed) {
      result.push(...genders);

      if (basicInfo.genderInfo) {
        result.push(basicInfo.genderInfo);
      }
    }

    return result;
  }

  private static mapVocalAge(
    model: IBasicInfoViewModel,
    basicInfo: BasicInfo,
    content: IBasicInfoConverterContent
  ): IBasicInfoViewModel {
    if (basicInfo.vocalAgeFrom && basicInfo.vocalAgeTo) {
      model.vocalAge = `${basicInfo.vocalAgeFrom} ${content.detailsConnectorTo} ${basicInfo.vocalAgeTo} ${content.detailsYears}`;
    } else if (basicInfo.vocalAgeFrom || basicInfo.vocalAgeTo) {
      model.vocalAge = `${basicInfo.vocalAgeFrom || basicInfo.vocalAgeTo} ${
        content.detailsYears
      }`;
    }

    return model;
  }

  private static mapVocalRange(
    model: IBasicInfoViewModel,
    basicInfo: BasicInfo,
    facets: IFacetsViewModel,
    content: IBasicInfoConverterContent
  ): IBasicInfoViewModel {
    let lowerNote = "";
    let upperNote = "";

    const lowerNoteMeasurement = basicInfo.measurements.find(
      (item) => item.measurementType === MeasurementTypes.LowerVocalNote
    );

    if (lowerNoteMeasurement) {
      const musicalNote = MUSICAL_NOTES.find(
        (item) => item.value === lowerNoteMeasurement.value
      );
      lowerNote = musicalNote ? musicalNote.name : "";
    }

    const upperNoteMeasurement = basicInfo.measurements.find(
      (item) => item.measurementType === MeasurementTypes.UpperVocalNote
    );

    if (upperNoteMeasurement) {
      const musicalNote = MUSICAL_NOTES.find(
        (item) => item.value === upperNoteMeasurement.value
      );
      upperNote = musicalNote ? musicalNote.name : "";
    }

    const beltNoteFacet = facets.beltNotes.facets.find((item) =>
      basicInfo.facets.some((facet) => facet.facetId === item.id)
    );
    const beltNote = beltNoteFacet
      ? ` (${content.detailsBeltTo} ${beltNoteFacet.name})`
      : "";

    const upperNoteWithConnector = lowerNote
      ? ` ${content.detailsConnectorTo} ${upperNote}`
      : upperNote;

    model.vocalRange = `${lowerNote}${upperNoteWithConnector}${beltNote}`;

    return model;
  }

  private static mapViewFacets(
    model: IBasicInfoViewModel,
    basicInfo: BasicInfo,
    facets: IFacetsViewModel
  ): IBasicInfoViewModel {
    const selectedGenderFacets: IFacetViewModel[] = [];

    basicInfo.facets.forEach((selectedFacet) => {
      Object.keys(facets).forEach((categoryKey) => {
        const category: IFacetCategoryViewModel = facets[categoryKey];
        const facet = category.facets.find(
          (item) => item.id === selectedFacet.facetId
        );

        if (!facet) {
          return;
        }

        const keyInMultiple = Object.keys(
          BASIC_INFO_FORM_MULTIPLE_FACETS_MAPPING
        ).find((key) => key === category.systemName);
        const keyInSingle =
          !keyInMultiple &&
          Object.keys(BASIC_INFO_FORM_SINGLE_FACETS_MAPPING).find(
            (key) => key === category.systemName
          );

        const value = facet.name;

        if (keyInMultiple) {
          const modelKey =
            BASIC_INFO_FORM_MULTIPLE_FACETS_MAPPING[category.systemName];

          if (keyInMultiple !== FacetCategories.Gender) {
            if (model[modelKey]) {
              model[modelKey].push(value);
            } else {
              model[modelKey] = [value];
            }
          } else {
            selectedGenderFacets.push(facet);
          }
        } else if (keyInSingle) {
          const modelKey =
            BASIC_INFO_FORM_SINGLE_FACETS_MAPPING[category.systemName];

          model[modelKey] = value;
        }
      });
    });

    if (selectedGenderFacets.length > 0) {
      for (const genderCode of GENDER_CODES_IN_PRIORITY) {
        const genderFacet = selectedGenderFacets.find(
          (facet) => facet.code === genderCode
        );

        if (genderFacet) {
          model.genders = [genderFacet.name];
          break;
        }
      }
    }

    return model;
  }

  private static mapViewMeasurements(
    model: IBasicInfoViewModel,
    basicInfo: BasicInfo,
    content: IBasicInfoConverterContent
  ): IBasicInfoViewModel {
    basicInfo.measurements.forEach((measurement) => {
      const modelKey =
        BASIC_INFO_FORM_MEASUREMENTS_MAPPING[measurement.measurementType];

      if (modelKey) {
        if (
          BASIC_INFO_CONVERTIBLE_LENGTH_MEASUREMENTS_MAPPING[
            measurement.measurementType
          ]
        ) {
          model[modelKey] = this.centimetersToInchesString(measurement.value);
        } else if (
          BASIC_INFO_CONVERTIBLE_WEIGHT_MEASUREMENTS_MAPPING[
            measurement.measurementType
          ]
        ) {
          model[modelKey] = this.kilogramsToImperialString(
            measurement.value,
            content.detailsStones,
            content.detailsPounds
          );
        } else if (
          BASIC_INFO_CONVERTIBLE_HEIGHT_MEASUREMENTS_MAPPING[
            measurement.measurementType
          ]
        ) {
          model[modelKey] =
            this.centimetersToImperialString(measurement.value) +
            ` (${measurement.value}cm)`;
        } else {
          model[modelKey] = measurement.value.toString();
        }
      }
    });

    return model;
  }

  private static centimetersToInchesString(centimeters: number): string {
    const inches = Math.round(centimeters * 0.3937);

    return `${inches}"`;
  }

  private static kilogramsToImperialString(
    kilograms: number,
    stonesName: string,
    poundsName: string
  ): string {
    const realPounds = Math.round(kilograms * 2.204623);
    const pounds = realPounds % 14;
    const stones = (realPounds - pounds) / 14;

    return `${stones} ${stonesName} ${pounds} ${poundsName}`;
  }

  private static centimetersToImperialString(centimeters: number): string {
    const realInches = Math.round(centimeters * 0.3937);
    const inches = realInches % 12;
    const feet = (realInches - inches) / 12;

    const feetText = feet <= 1 ? "foot" : "feet";
    const inchText = inches ? (inches <= 1 ? "inch" : "inches") : "";

    return `${feet} ${feetText} ${inches} ${inchText}`;
  }
}
